import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Clock, Shield, Code, AlertCircle, CheckCircle2, Mic, MicOff } from "lucide-react";
import MicrophoneTest from "./components/MicrophoneTest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TALandingPage — Pre-assessment landing page for TA/HOS
 * Shows candidate info, assessment rules, time limits, and a start button.
 */
export default function TALandingPage() {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [verified, setVerified] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [micTestStatus, setMicTestStatus] = useState(null); // null | "passed" | "skipped"

  const searchParams = new URLSearchParams(window.location.search);
  const isQAMode = searchParams.get("qa") === "true";
  const adminToken = searchParams.get("admin_token") || "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const qaParam = isQAMode ? `?qa=true&admin_token=${adminToken}` : "";
        const res = await fetch(`${API}/dynamic-assessments/take/${accessToken}${qaParam}`);
        if (!res.ok) {
          // Try to extract the detail from the response body
          let detail = "";
          try {
            const errorBody = await res.json();
            detail = errorBody.detail || "";
          } catch {
            detail = "";
          }
          
          if (res.status === 403) {
            throw new Error(detail || "This assessment link is no longer active. A new link will be shared with you shortly. For questions, please contact helpsagicor@thcohq.com.");
          }
          if (res.status === 400) {
            // 400 from this endpoint means completed or expired
            throw new Error(detail || "This assessment has already been completed");
          }
          throw new Error(detail || "Assessment not found");
        }
        const data = await res.json();
        setAssessmentData(data);
        // In QA mode, auto-verify so admin skips email check
        if (data.qaMode) {
          setVerified(true);
          setMicTestStatus("skipped");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accessToken, isQAMode, adminToken]);

  const handleVerify = () => {
    setVerifyError("");
    const candidateEmail = assessmentData?.candidate?.email?.toLowerCase()?.trim();
    if (emailInput.toLowerCase().trim() === candidateEmail) {
      setVerified(true);
    } else {
      setVerifyError("Email does not match. Please enter the email this assessment was assigned to.");
      // Log failure for diagnostics (fire-and-forget)
      fetch(`${API}/dynamic-assessments/take/${accessToken}/log-verify-failure`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_token: accessToken,
          submitted_email: emailInput,
          stored_email: assessmentData?.candidate?.email || "",
        }),
      }).catch(() => {});
    }
  };

  const handleStart = () => {
    // Set verification flag so DynamicAssessmentForm knows the candidate
    // passed email verification + mic test gate
    sessionStorage.setItem(`ta_verified_${accessToken}`, 'true');
    const qaParams = isQAMode ? `?qa=true&admin_token=${adminToken}` : "";
    navigate(`/dynamic-assessment/${accessToken}${qaParams}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 animate-spin text-[#00AEEF]" />
      </div>
    );
  }

  if (error) {
    const isDisabled = error.includes("no longer active");
    const isCompleted = error.includes("already been completed");
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            {isDisabled ? (
              <>
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-amber-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2" data-testid="assessment-disabled-title">Assessment Link Inactive</h2>
                <p className="text-gray-600 mb-4">This assessment link is no longer active. A new link will be shared with you shortly.</p>
                <p className="text-sm text-gray-500">For questions, please contact <a href="mailto:helpsagicor@thcohq.com" className="text-[#00AEEF] underline">helpsagicor@thcohq.com</a></p>
              </>
            ) : isCompleted ? (
              <>
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-slate-500" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2" data-testid="assessment-session-ended-title">Assessment Session Ended</h2>
                <p className="text-gray-600 mb-4">Your assessment session has ended. If you were unable to complete your assessment or experienced a technical issue, please contact your assessment coordinator at{" "}
                  <a href="mailto:helpsagicor@thcohq.com" className="text-[#00AEEF] underline">helpsagicor@thcohq.com</a>{" "}
                  and we will assist you.</p>
              </>
            ) : (
              <>
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-800 mb-2" data-testid="assessment-error-title">Unable to Load</h2>
                <p className="text-gray-600">{error}</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Paused assessment overlay
  if (assessmentData?.paused || assessmentData?.status === 'paused') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="assessment-paused-overlay">
        <div className="max-w-lg w-full">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-10 text-center shadow-2xl">
            <div className="w-20 h-20 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white">Currently Unavailable</h1>
          </div>
        </div>
      </div>
    );
  }

  const candidate = assessmentData?.candidate || {};
  const doc = assessmentData?.template?.documentStructure || {};
  const metadata = doc.metadata || {};
  const assessmentType = candidate.assessmentType;
  const isTA = assessmentType === "technical_assessment";
  const isHOS = assessmentType === "hands_on_simulation";
  const totalTime = metadata.total_time_seconds || candidate.timeLimitTotal || null;
  const totalMinutes = totalTime ? Math.ceil(totalTime / 60) : null;

  // Gate enforcement removed — HOS always accessible regardless of TA status

  // Count sections with time limits
  const sections = (doc.content || []).filter((i) => i.type === "section");

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="ta-landing-page">
      {isQAMode && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium" data-testid="qa-mode-landing-banner">
          QA ACCESS MODE — Viewing as admin. Email verification and mic test skipped. Status: {assessmentData?.status}
        </div>
      )}
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-4 ${
            isHOS ? "bg-purple-100 text-purple-700" : "bg-[#00AEEF]/10 text-[#00AEEF]"
          }`}>
            <Code className="w-4 h-4" />
            {isTA ? "Technical Assessment" : isHOS ? "Hands-On Simulation" : "Assessment"}
          </div>
          <h1 className="text-2xl font-bold text-[#1B3A5C] mb-2" data-testid="ta-landing-title">
            {metadata.title || `Sagicor Technology Capability Assessment`}
          </h1>
          <p className="text-gray-600">
            {candidate.jobFamily && (
              <span className="capitalize">{candidate.jobFamily.replace(/_/g, " ")}</span>
            )}
            {candidate.gradeTier && (
              <span className="capitalize"> — {candidate.gradeTier.replace(/_/g, " ")} Level</span>
            )}
          </p>
        </div>

        {/* Candidate info card */}
        <Card className="mb-6">
          <CardContent className="pt-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Candidate</span>
                <p className="font-medium text-[#3b3b3b]">{candidate.fullName}</p>
              </div>
              <div>
                <span className="text-gray-500">Assessment ID</span>
                <p className="font-medium text-[#3b3b3b]">{candidate.assessmentId}</p>
              </div>
              {sections.length > 0 && (
                <div>
                  <span className="text-gray-500">Sections</span>
                  <p className="font-medium text-[#3b3b3b]">{sections.length}</p>
                </div>
              )}
              {totalMinutes && (
                <div>
                  <span className="text-gray-500">Time Limit</span>
                  <p className="font-medium text-[#3b3b3b]">{totalMinutes} minutes</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Rules */}
        <Card className="mb-6">
          <CardContent className="pt-5">
            <h3 className="font-semibold text-[#1B3A5C] mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Assessment Rules
            </h3>
            <ul className="space-y-2 text-sm text-[#3b3b3b]">
              {totalMinutes && (
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-[#00AEEF] mt-0.5 flex-shrink-0" />
                  <span>You have <strong>{totalMinutes} minutes</strong> to complete this assessment. The timer starts when you begin answering.</span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <Mic className="w-4 h-4 text-[#00AEEF] mt-0.5 flex-shrink-0" />
                <span>This assessment supports <strong>voice responses</strong>. You can type your answers, record a voice response, or use both. Click the microphone icon on any question to record.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#00AEEF] mt-0.5 flex-shrink-0" />
                <span>Your responses are saved automatically every 30 seconds.</span>
              </li>
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-[#00AEEF] mt-0.5 flex-shrink-0" />
                <span>Tab switches and copy/paste activity are monitored for integrity purposes.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#00AEEF] mt-0.5 flex-shrink-0" />
                <span>Once submitted, you cannot modify your responses.</span>
              </li>
            </ul>

            {/* Technical Requirements */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 font-medium mb-1.5">Technical Requirements</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>Desktop or laptop browser (Chrome, Edge, Firefox, Safari)</li>
                <li>Stable internet connection</li>
                <li>Microphone access (optional, for voice responses)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Verification */}
        {!verified ? (
          <Card className="mb-6" data-testid="ta-verify-section">
            <CardContent className="pt-5">
              <h3 className="font-semibold text-[#1B3A5C] mb-3">Verify Your Identity</h3>
              <p className="text-sm text-gray-600 mb-3">
                Enter the email address this assessment was assigned to:
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="your.email@company.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleVerify()}
                  className="flex-1"
                  data-testid="ta-email-input"
                />
                <Button onClick={handleVerify} data-testid="ta-verify-btn">
                  Verify
                </Button>
              </div>
              {verifyError && (
                <p className="text-xs text-red-500 mt-2" data-testid="ta-verify-error">{verifyError}</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="text-center" data-testid="ta-start-section">
            <div className="inline-flex items-center gap-2 text-sm text-green-600 mb-4">
              <CheckCircle2 className="w-4 h-4" /> Identity verified
            </div>

            {/* Mic test — must pass or skip before proceeding */}
            <div className="mb-6 max-w-md mx-auto text-left">
              <MicrophoneTest token={accessToken} onResult={(status) => setMicTestStatus(status)} />
            </div>

            <Button
              size="lg"
              onClick={handleStart}
              disabled={!micTestStatus}
              className="bg-[#00AEEF] hover:bg-[#0090c5] text-white px-8 h-12 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ta-begin-btn"
            >
              Begin Assessment
            </Button>
            {!micTestStatus && (
              <p className="text-xs text-amber-500 mt-2">
                Complete the microphone test above before starting
              </p>
            )}
            {totalMinutes && micTestStatus && (
              <p className="text-xs text-gray-400 mt-2">
                Timer starts when you begin answering questions
              </p>
            )}
          </div>
        )}
      </div>

      <footer className="border-t bg-white mt-12 py-6 text-center">
        <p className="text-xs text-gray-400 mb-1">Support: helpsagicor@thcohq.com</p>
        <p className="text-sm text-gray-500">© 2026 THCO</p>
      </footer>
    </div>
  );
}
